import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { employeeAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Building2, CheckCircle, XCircle, Clock, Loader2, ArrowLeft, 
  Shield, Users, AlertTriangle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AccessRequests = () => {
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchAccessRequests();
  }, []);

  const fetchAccessRequests = async () => {
    try {
      setLoading(true);
      const [pendingRes, allRes] = await Promise.all([
        employeeAPI.getAccessRequests(),
        employeeAPI.getAllAccessRequests()
      ]);
      setPendingRequests(pendingRes.data.requests || []);
      setAllRequests(allRes.data.requests || []);
    } catch (error) {
      console.error('Error fetching access requests:', error);
      toast.error('Failed to load access requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (requestId, action) => {
    setProcessingId(requestId);
    try {
      await employeeAPI.respondToAccessRequest(requestId, action);
      toast.success(action === 'approve' ? 'Access approved' : 'Request declined');
      fetchAccessRequests();
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500/20 text-green-400 border-0">Approved</Badge>;
      case 'declined':
        return <Badge className="bg-red-500/20 text-red-400 border-0">Declined</Badge>;
      case 'revoked':
        return <Badge className="bg-gray-500/20 text-gray-400 border-0">Revoked</Badge>;
      default:
        return <Badge className="bg-amber-500/20 text-amber-400 border-0">Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6" data-testid="access-requests-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/employee/settings" className="text-[#5B6472] hover:text-[#0A0F1E]">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Access Requests</h1>
            <p className="mt-1 text-[#5B6472] text-sm">Manage employer requests to view your full records</p>
          </div>
        </div>

        {/* Info Card */}
        <Card className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-blue-400 font-medium text-sm">Selective Visibility Active</p>
              <p className="text-[#5B6472] text-sm mt-1">
                When you approve an employer's request, they can view your complete work records 
                including detailed assessments and professional closing statements.
              </p>
            </div>
          </div>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#FFFFFF] border border-[#E8EDF5]">
            <TabsTrigger 
              value="pending" 
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg relative"
            >
              Pending
              {pendingRequests.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-[#1B5AFF] text-white text-xs">
                  {pendingRequests.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
            >
              History
            </TabsTrigger>
          </TabsList>

          {/* Pending Requests */}
          <TabsContent value="pending" className="mt-6">
            {pendingRequests.length === 0 ? (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-12 text-center">
                <Users className="w-12 h-12 mx-auto text-[#5B6472]/50 mb-4" />
                <p className="text-[#0A0F1E] font-medium">No Pending Requests</p>
                <p className="text-[#5B6472] text-sm mt-1">
                  When employers request access to your full records, they'll appear here
                </p>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingRequests.map((request) => (
                  <Card
                    key={request.id}
                    className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#F5F8FF] flex items-center justify-center flex-shrink-0">
                          {request.company_logo ? (
                            <img 
                              src={request.company_logo} 
                              alt="" 
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                          ) : (
                            <Building2 className="w-6 h-6 text-[#5B6472]" />
                          )}
                        </div>
                        <div>
                          <p className="text-[#0A0F1E] font-medium">{request.company_name}</p>
                          {request.company_industry && (
                            <p className="text-[#5B6472] text-sm">{request.company_industry}</p>
                          )}
                          <p className="text-[#5B6472] text-xs mt-1">
                            Requested {formatDate(request.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleRespond(request.id, 'decline')}
                          disabled={processingId === request.id}
                          variant="outline"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl"
                        >
                          {processingId === request.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 mr-2" />
                              Decline
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleRespond(request.id, 'approve')}
                          disabled={processingId === request.id}
                          className="bg-green-500 hover:bg-green-600 hover:bg-green-700 text-[#FFFFFF] rounded-xl"
                        >
                          {processingId === request.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Request History */}
          <TabsContent value="history" className="mt-6">
            {allRequests.length === 0 ? (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-12 text-center">
                <Clock className="w-12 h-12 mx-auto text-[#5B6472]/50 mb-4" />
                <p className="text-[#0A0F1E] font-medium">No Request History</p>
                <p className="text-[#5B6472] text-sm mt-1">
                  Your access request history will appear here
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {allRequests.map((request) => (
                  <Card
                    key={request.id}
                    className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#F5F8FF] flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-[#5B6472]" />
                        </div>
                        <div>
                          <p className="text-[#0A0F1E] font-medium text-sm">{request.company_name}</p>
                          <p className="text-[#5B6472] text-xs">
                            {formatDate(request.created_at)}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AccessRequests;
